'use client'
import React from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Image from 'next/image'
import { Strings } from '@/lib/constants/strings'
import { TypographyVariants } from '@/app/styles/themes/typography'
import SupportErrorText from './supportErrorText'

export enum ErrorCardVariant {
  UNAUTHORIZED = 'unauthorized',
  AUTH_ERROR = 'authError',
  NETWORK_ERROR = 'networkError',
}

interface VariantConfig {
  title: string
  subtitle: string
  iconSrc: string
  primaryButtonLabel: string
  testId: string
  buttonTestId: string
}

const variantConfigs: Record<ErrorCardVariant, VariantConfig> = {
  [ErrorCardVariant.UNAUTHORIZED]: {
    title: Strings.UNAUTHORIZED_ACCESS_TITLE,
    subtitle: Strings.UNAUTHORIZED_ACCESS_SUBTITLE,
    iconSrc: '/icons/errors/error-permissions.svg',
    primaryButtonLabel: Strings.SIGNOUT_BUTTON,
    testId: 'unauthorized-error-view',
    buttonTestId: 'unauthorized-logout-button',
  },
  [ErrorCardVariant.AUTH_ERROR]: {
    title: Strings.AUTH_ERROR_TITLE,
    subtitle: Strings.AUTH_ERROR_SUBTITLE,
    iconSrc: '/icons/error.svg',
    primaryButtonLabel: Strings.AUTH_ERROR_RETRY_BUTTON,
    testId: 'auth-error-view',
    buttonTestId: 'auth-error-retry-button',
  },
   [ErrorCardVariant.NETWORK_ERROR]: {
    title: Strings.NETWORK_ERROR_TITLE,
    subtitle: Strings.NETWORK_ERROR_SUBTITLE,
    iconSrc: '/icons/error.svg',
    primaryButtonLabel: Strings.NETWORK_ERROR_RETRY_BUTTON,
    testId: 'network-error-view',
    buttonTestId: 'network-error-retry-button',
  },
}

interface ErrorCardViewProps {
  variant: ErrorCardVariant
  onPrimaryAction: () => void
  primaryButtonLabel?: string
  buttonTestId?: string
  supportLines?: string[]
  supportTextTestId?: string
}

const ErrorCardView: React.FC<ErrorCardViewProps> = ({
  variant,
  onPrimaryAction,
  primaryButtonLabel,
  buttonTestId,
  supportLines,
  supportTextTestId,
}) => {
  const theme = useTheme()
  const config = variantConfigs[variant]
  const resolvedButtonLabel = primaryButtonLabel ?? config.primaryButtonLabel
  const resolvedButtonTestId = buttonTestId ?? config.buttonTestId

  return (
    <Grid
      container
      justifyItems={'center'}
      justifyContent="center"
      alignItems="center"
      direction="column"
      alignSelf="center"
      minHeight={'100vh'}
      sx={{ mt: '-62px', pt: '56px' }}
    >
      <Box
        data-testid={config.testId}
        sx={{
          mx: 'auto',
          textAlign: 'center',
          bgcolor: theme.palette.surface.neutral.t1Base,
          border: `1px solid ${theme.palette.stroke.neutral.low}`,
          borderBottom: `1px solid ${theme.palette.stroke.neutral.low}`,
          borderRadius: '8px',
          p: { xs: '24px', sm: '36px' },
          pb: 4,
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Image
          src="/icons/bi_icons/broker-workplace.svg"
          alt="broker-workplace-icon"
          width={160}
          height={66}
          data-testid={'broker-workplace-icon'}
          aria-label={'broker-workplace-icon'}
          style={{ margin: 'auto', display: 'block' }}
        />
        <Box sx={{ mt: 4 }}>
          <Image
            src={config.iconSrc}
            alt={config.title}
            width={200}
            height={200}
            data-testid={'error-card-icon'}
            aria-label={config.title}
          />
        </Box>

        <Typography variant={TypographyVariants.mediumSemiBold} sx={{ color: theme.palette.surface.brand }}>
          {config.title}
        </Typography>
        <Typography
          variant={TypographyVariants.baseRegular}
          sx={{
            mt: 1,
            color: theme.palette.surface.brand,
          }}
        >
          {config.subtitle}
        </Typography>
        <Button
          data-testid={resolvedButtonTestId}
          size="medium"
          variant="contained"
          color="primary"
          onClick={onPrimaryAction}
          autoFocus
          sx={{
            mt: 4,
            boxShadow: 'none !important',
          }}
        >
          {resolvedButtonLabel}
        </Button>
        {supportLines && <SupportErrorText supportLines={supportLines} dataTestId={supportTextTestId} />}
      </Box>
    </Grid>
  )
}

export default ErrorCardView
